/**
 * Lottie art is exported with its ink baked in, and ours is drawn for a light
 * page: `measure.json`, the tape-measure illustration above the quoting form,
 * outlines its tan-and-orange shapes in near-black, which disappears on a dark
 * background. The colours themselves are fine — only the ink needs to flip.
 *
 * So rather than maintain a second copy of each asset, we walk the parsed
 * animation and repaint any colour stop dark enough to be ink. Everything
 * lighter is left alone, which is what keeps the white glyphs inside the
 * coloured badge animations (`error.json`, `success-blue.json`) untouched.
 */

/** Below this relative luminance a colour stop is treated as outline ink. */
const INK_LUMINANCE_CEILING = 0.12;

/** A node of a parsed Lottie document. */
export type LottieNode =
  | string
  | number
  | boolean
  | LottieNode[]
  | { [key: string]: LottieNode };

/**
 * Relative luminance of a Lottie colour — a 0–1 RGBA array. Returns undefined
 * for anything that isn't one, since `k` also carries sizes, positions and
 * keyframe objects.
 */
function colorLuminance(value: LottieNode): number | undefined {
  if (!Array.isArray(value) || value.length !== 4) return undefined;
  const [red, green, blue] = value;
  if (
    typeof red !== "number" ||
    typeof green !== "number" ||
    typeof blue !== "number"
  ) {
    return undefined;
  }
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function alphaOf(value: LottieNode): number {
  if (!Array.isArray(value)) return 1;
  const alpha = value[3];
  return typeof alpha === "number" ? alpha : 1;
}

/**
 * Returns a copy of `animationData` with its ink repainted to `ink` (an
 * `[r, g, b]` triple in Lottie's 0–1 space). Alpha is preserved, so a
 * semi-transparent outline stays semi-transparent.
 */
export function repaintAnimationInk(
  animationData: LottieNode,
  ink: readonly [number, number, number],
): LottieNode {
  if (Array.isArray(animationData)) {
    return animationData.map((entry) => repaintAnimationInk(entry, ink));
  }
  if (!animationData || typeof animationData !== "object") {
    return animationData;
  }
  const result: { [key: string]: LottieNode } = {};
  for (const [key, value] of Object.entries(animationData)) {
    // A flat colour is always `{ a: 0, k: [r, g, b, a] }` under a fill, stroke
    // or gradient stop. Animated colours (`a: 1`) hold keyframes instead, so
    // they fall through to the recursive walk and their own `k` arrays are
    // reached the same way.
    const luminance = key === "k" ? colorLuminance(value) : undefined;
    if (luminance !== undefined && luminance <= INK_LUMINANCE_CEILING) {
      result[key] = [ink[0], ink[1], ink[2], alphaOf(value)];
    } else {
      result[key] = repaintAnimationInk(value, ink);
    }
  }
  return result;
}

/**
 * Ink for the current theme, in Lottie's 0–1 space. Light mode keeps the near
 * black the art already used (`--foreground`); dark mode uses the dark
 * `--foreground`, so outlines read as light strokes over the illustration's own
 * colours.
 */
export function animationInkForTheme(
  isDark: boolean,
): readonly [number, number, number] {
  return isDark ? [0.894, 0.894, 0.906] : [0.129, 0.157, 0.22];
}
